/**
 * Workspace sections for Prospeção (CCC) — first-contact pipeline.
 *
 * Labels here are Prospeção-specific (e.g. "Entrada" instead of "Por contactar"
 * / "Iniciado/Atribuído", and "Perdido" instead of "Sem Interesse" in
 * Em Tratamento). Substage IDs are kept aligned with the Recuperação workspace
 * so existing CccProspeccaoLead rows keep matching their bucket without a
 * data migration.
 */

export type ProspeccaoSectionKey = 'por_contactar' | 'em_tratamento' | 'pre_fecho' | 'fecho';

export type ProspeccaoSubsection = {
  substage: string;
  label: string;
};

export type ProspeccaoSection = {
  key: ProspeccaoSectionKey;
  title: string;
  accent: string;
  subsections: ProspeccaoSubsection[];
};

export const PROSPECCAO_SECTIONS: ProspeccaoSection[] = [
  {
    key: 'por_contactar',
    title: 'Entrada',
    accent: '#2563eb',
    subsections: [
      { substage: 'por-contactar-iniciado-atribuido', label: 'Entrada' },
      { substage: 'por-contactar-nao-atendido', label: 'Não Atendido' },
      { substage: 'por-contactar-incontactavel', label: 'Incontactável' },
      { substage: 'por-contactar-responsavel-ausente', label: 'Responsável Ausente' },
      { substage: 'por-contactar-callback', label: 'Callback' },
      { substage: 'por-contactar-cliente-edp', label: 'Cliente EDP' },
      { substage: 'por-contactar-sem-interesse', label: 'Sem Interesse' },
    ],
  },
  {
    key: 'em_tratamento',
    title: 'Em Tratamento',
    accent: '#8b5cf6',
    subsections: [
      { substage: 'em-tratamento-aguardar-fatura', label: 'A aguardar Fatura' },
      { substage: 'em-tratamento-aguardar-proposta-bo', label: 'A aguardar Proposta BO' },
      { substage: 'em-tratamento-sem-interesse', label: 'Perdido' },
      { substage: 'em-tratamento-pendente', label: 'Pendente' },
      { substage: 'em-tratamento-fidelizados', label: 'Fidelizados' },
    ],
  },
  {
    key: 'pre_fecho',
    title: 'Pré-Fecho',
    accent: '#2563eb',
    subsections: [
      { substage: 'pre-fecho-proposta-enviada', label: 'Proposta Enviada' },
      { substage: 'pre-fecho-assinado', label: 'Assinatura' },
    ],
  },
  {
    key: 'fecho',
    title: 'Fecho',
    accent: '#10b981',
    subsections: [
      { substage: 'fecho-pendente-documentos', label: 'Pendente Documentos' },
      { substage: 'fecho-contrato-submetido', label: 'Contrato Submetido' },
    ],
  },
];

export const PROSPECCAO_ALL_SUBSTAGES = PROSPECCAO_SECTIONS.flatMap((section) =>
  section.subsections.map((sub) => sub.substage)
);

/** Default substage when a Prospeção lead is created. */
export const PROSPECCAO_DEFAULT_SUBSTAGE = 'por-contactar-iniciado-atribuido';

/** Substage before opening Solicitar Proposta (same ID as Recuperação workspace). */
export const PROSPECCAO_SUBSTAGE_AGUARDAR_PROPOSTA_BO = 'em-tratamento-aguardar-proposta-bo';

/** Substage where CCC submits contracts (Gestão das Marcações → Contrato Submetido). */
export const PROSPECCAO_SUBSTAGE_CONTRATO_SUBMETIDO = 'fecho-contrato-submetido';

/**
 * Map legacy / Recuperação Comercial substages into current Prospeção buckets.
 * Existing leads created before this config are bucketed safely and any future
 * legacy values land in the "Entrada" sub-section.
 */
const LEGACY_SUBSTAGE_TO_BUCKET: Record<string, string> = {
  'agendamentos-agendamentos': 'por-contactar-iniciado-atribuido',
  'agendamentos-remarcacao': 'por-contactar-nao-atendido',
  'reuniao-realizada-reuniao': 'por-contactar-incontactavel',
  'feedback-pendencia-not-ok-cliente': 'em-tratamento-sem-interesse',
  'feedback-pendencia-not-ok-callcenter': 'em-tratamento-aguardar-proposta-bo',
  'feedback-pendencia-pendente': 'em-tratamento-pendente',
  'feedback-pendencia-concluido-sem-proposta': 'em-tratamento-sem-interesse',
  'feedback-pendencia-solicitar-proposta': 'pre-fecho-proposta-enviada',
  'feedback-pendencia-concluido-com-proposta': 'pre-fecho-assinado',
  'pre-fecho-aguardar-assinatura-cliente': 'pre-fecho-assinado',
  'pre-fecho-fidelizado-f': 'pre-fecho-assinado',
  'pre-fecho-perdido-sem-assinatura': 'pre-fecho-assinado',
  'resultado-fecho-ok': 'fecho-contrato-submetido',
  'resultado-fecho-not-ok': 'fecho-pendente-documentos',
  'resultado-aguardar': 'fecho-pendente-documentos',
  'resultado-cancelado': 'fecho-pendente-documentos',
};

export function normalizeProspeccaoSubstage(substage: string): string {
  return LEGACY_SUBSTAGE_TO_BUCKET[substage] || substage;
}

export function prospeccaoSectionForSubstage(
  substage: string
): ProspeccaoSection | undefined {
  const normalized = normalizeProspeccaoSubstage(substage);
  return PROSPECCAO_SECTIONS.find((section) =>
    section.subsections.some((sub) => sub.substage === normalized)
  );
}

export function prospeccaoSubstageLabel(substage: string): string {
  const normalized = normalizeProspeccaoSubstage(substage);
  for (const section of PROSPECCAO_SECTIONS) {
    const sub = section.subsections.find((s) => s.substage === normalized);
    if (sub) return sub.label;
  }
  return substage;
}
